<template>
  <a-card class="quote-margin">
    <v-col>
      <div class="quote-section-title">Margin</div>

      <div class="quote-section-block quote-margin-block">
        <div class="quote-margin-option quote-margin-option--fixed">
          <label class="quote-margin-option__header">
            <input
              type="radio"
              value="fixed"
              v-model="data.margin.status"
              :disabled="readonly"
              @input="(e) => onStatusChanged(e.target.value)"
            />
            <span>Apply a fixed margin</span>
          </label>
          <div class="quote-margin-field">
            <label class="quote-margin-label">In percent</label>
            <div class="quote-margin-inline">
              <f-number
                v-model="data.margin.percentFixed"
                smlabel
                :disabled="readonly || data.margin.status != 'fixed'"
                @input="(v) => onPercentFixedInput(v)"
              />
              <span class="quote-margin-unit">%</span>
            </div>
          </div>
        </div>

        <div class="quote-margin-modes">
          <label class="quote-margin-mode">
            <input
              type="radio"
              value="dynamic"
              v-model="data.margin.status"
              :disabled="readonly"
              @input="(e) => onStatusChanged(e.target.value)"
            />
            <span>Apply a percentage margin</span>
          </label>
          <label class="quote-margin-mode">
            <input
              type="radio"
              value="perGroup"
              v-model="data.margin.status"
              :disabled="readonly"
              @input="(e) => onStatusChanged(e.target.value)"
            />
            <span>Apply a dollar margin per group</span>
          </label>
        </div>

        <div class="quote-margin-table-row">
          <div class="quote-margin-table-wrap">
            <table class="quote-margin-table">
              <thead>
                <tr>
                  <th>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <span class="quote-margin-help-label" v-bind="attrs" v-on="on">PAX range</span>
                      </template>
                      <span>Group size range used for this row’s margin calculation.</span>
                    </v-tooltip>
                  </th>
                  <th>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <span class="quote-margin-help-label" v-bind="attrs" v-on="on">Margin (%)</span>
                      </template>
                      <span>Effective percentage margin for this PAX range. Editable when “percentage margin” mode is selected; shows the fixed % when fixed margin is selected.</span>
                    </v-tooltip>
                  </th>
                  <th>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <span class="quote-margin-help-label" v-bind="attrs" v-on="on">Margin ({{ getCurrency() }} / group)</span>
                      </template>
                      <span>Fixed margin amount added per group for this PAX range when “$ per group” mode is selected.</span>
                    </v-tooltip>
                  </th>
                  <th>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <span class="quote-margin-help-label" v-bind="attrs" v-on="on">Commission ({{ getCurrency() }})</span>
                      </template>
                      <span>Calculated commission for this PAX range based on current costs and margin settings.</span>
                    </v-tooltip>
                  </th>
                  <th>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <span class="quote-margin-help-label" v-bind="attrs" v-on="on">Sell price / PAX ({{ getCurrency() }})</span>
                      </template>
                      <span>Final selling price per person for this PAX range (costs + margin and commission).</span>
                    </v-tooltip>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(peopleRange, index) in data.peopleRanges" :key="index">
                  <td class="quote-margin-table__range">{{ peopleRange.min }}-{{ peopleRange.max }}</td>
                  <td>
                    <div class="quote-margin-inline">
                      <f-number
                        :value="marginPercentForRow(index)"
                        smlabel
                        :disabled="readonly || data.margin.status != 'dynamic'"
                        @input="(v) => onPercentDynamicInput(index, v)"
                      />
                      <span class="quote-margin-unit">%</span>
                    </div>
                  </td>
                  <td>
                    <div class="quote-margin-inline">
                      <f-number
                        v-model="data.margin.perGroup[index]"
                        smlabel
                        :disabled="readonly || data.margin.status != 'perGroup'"
                        @input="(v) => onPerGroupInput(index, v)"
                      />
                      <span class="quote-margin-unit">{{ getCurrency() }}</span>
                    </div>
                  </td>
                  <td>
                    <div class="quote-margin-inline">
                      <f-number :value="commissions(peopleRange.min)" smlabel disabled />
                      <span class="quote-margin-unit">{{ getCurrency() }}</span>
                    </div>
                  </td>
                  <td>
                    <div class="quote-margin-inline">
                      <f-number :value="sellingPrice(index)" smlabel disabled />
                      <span class="quote-margin-unit">{{ getCurrency() }}</span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="quote-margin-supplements">
            <table class="quote-margin-table">
              <thead>
                <tr>
                  <th colspan="2">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <span class="quote-margin-help-label" v-bind="attrs" v-on="on">Supplement / Discount</span>
                      </template>
                      <span>Per-person adjustments from room costs for the largest PAX range, using margin and discount settings.</span>
                    </v-tooltip>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in supplementRows" :key="item.key">
                  <td class="quote-margin-table__label">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <span class="quote-margin-help-label" v-bind="attrs" v-on="on">{{ item.label }}</span>
                      </template>
                      <span>{{ item.help }}</span>
                    </v-tooltip>
                  </td>
                  <td>
                    <div class="quote-margin-inline">
                      <f-number
                        :value="item.value"
                        smlabel
                        :disabled="!item.editable || !item.isOverridden || supplementsDisabled"
                        @input="(v) => onSupplementInput(item.key, v)"
                        @blur="() => onSupplementBlur(item.key)"
                      />
                      <span class="quote-margin-unit">{{ getCurrency() }}</span>
                      <v-btn
                        v-if="item.editable"
                        icon
                        x-small
                        class="quote-margin-lock"
                        :disabled="supplementsDisabled"
                        :title="item.isOverridden ? 'Lock to calculated value' : 'Unlock to edit'"
                        @click="toggleSupplementEditable(item.key)"
                      >
                        <v-icon small>{{ item.isOverridden ? "mdi-lock-outline" : "mdi-lock-off-outline" }}</v-icon>
                      </v-btn>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </v-col>
  </a-card>
</template>

<script>
import Vue from "vue";
import quoteUtils from "@/modules/quotes/components/utils";
import moneyService from "@/services/money";
export default {
  components: {},
  props: {
    value: Object,
    supplierProductData: Object,
    readonly: {
      type: Boolean,
      default: false,
    },
    supplementsReadonly: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    data: {
      get() {
        return this.value;
      },
      set(newValue) {
        this.$emit("input", newValue);
      },
    },
    supplementsDisabled() {
      return this.readonly || this.supplementsReadonly;
    },
    supplement() {
      return quoteUtils.getSupplementValue(
        this.data.costs,
        this.data.peopleRanges,
        this.data.margin,
        this.data.discountMargin
      );
    },
    isTripleOverridden() {
      return quoteUtils.isSupplementOverrideSet(this.data.supplementTripleOverride);
    },
    isQuadOverridden() {
      return quoteUtils.isSupplementOverrideSet(this.data.supplementQuadOverride);
    },
    supplementSingle() {
      return quoteUtils.roundUpTo5(this.supplement.single);
    },
    supplementTwin() {
      return quoteUtils.roundUpTo5(this.supplement.twin);
    },
    supplementTriple() {
      return quoteUtils.getSupplementDisplayValue(
        this.supplement.triple,
        this.data.supplementTripleOverride,
        quoteUtils.roundDownTo5
      );
    },
    supplementQuad() {
      return quoteUtils.getSupplementDisplayValue(
        this.supplement.quad,
        this.data.supplementQuadOverride,
        quoteUtils.roundDownTo5
      );
    },
    supplementChild() {
      return quoteUtils.roundDownTo5(this.supplement.child);
    },
    supplementRows() {
      return [
        {
          key: "single",
          label: "Single Supplement",
          value: this.supplementSingle,
          editable: false,
          isOverridden: false,
          help:
            "Single room cost minus half the default double room cost (from all room products), rounded up to the nearest 5.",
        },
        {
          key: "twin",
          label: "Twin Supplement",
          value: this.supplementTwin,
          editable: false,
          isOverridden: false,
          help:
            "Per-person twin occupancy adjustment versus half the double cost, including the margin percentage, rounded up to the nearest 5.",
        },
        {
          key: "triple",
          label: "Triple Discount",
          value: this.supplementTriple,
          editable: true,
          isOverridden: this.isTripleOverridden,
          help: this.isTripleOverridden
            ? "Manually overridden Triple Discount for this period. Lock to restore the calculated value."
            : "Per-person triple occupancy adjustment versus half the double cost, after the discount margin, rounded down to the nearest 5. Unlock to override this value for the selected period.",
        },
        {
          key: "quad",
          label: "Quad Discount",
          value: this.supplementQuad,
          editable: true,
          isOverridden: this.isQuadOverridden,
          help: this.isQuadOverridden
            ? "Manually overridden Quad Discount for this period. Lock to restore the calculated value."
            : "Per-person quad occupancy adjustment versus half the double cost, after the discount margin, rounded down to the nearest 5. Unlock to override this value for the selected period.",
        },
        {
          key: "child",
          label: "Child discount (<12 years)",
          value: this.supplementChild,
          editable: false,
          isOverridden: false,
          help:
            "Child rate derived from the single supplement with margin removed (shown as a discount), rounded down to the nearest 5.",
        },
      ];
    },
  },
  methods: {
    marginPercentForRow(index) {
      if (this.data.margin?.status === "fixed") {
        return this.data.margin.percentFixed;
      }
      return this.data.margin?.percentDynamic?.[index];
    },
    sellingPrice(index) {
      return quoteUtils.roundToCustom(this.data.margin.perPax[index]);
    },
    commissions(min) {
      return quoteUtils.getCommissions(this.data.costs, min).toFixed(0);
    },
    onStatusChanged(v) {
      if (this.readonly) return;
      this.data.margin.status = v;
      this.updateMargins();
    },
    onPercentFixedInput(v) {
      if (this.readonly) return;
      this.updateMargins();
    },
    onPercentDynamicInput(index, v) {
      if (this.readonly) return;
      Vue.set(this.data.margin.percentDynamic, index, v);
      Vue.set(this.data.margin.perGroup, index, this.getPerGroupByMinimumOfRange(index));
      Vue.set(this.data.margin.perPax, index, this.getPerPaxByMinimumOfRange(index));
    },
    onPerGroupInput(index, v) {
      if (this.readonly) return;
      const { margin } = this.data;
      const { status, perGroup, perPax } = margin;
      const perGroupValue = perGroup[index];

      if (status === "fixed") {
        margin.percentFixed = this.calculatePercentByPerPax(index, perGroupValue);
        perPax.forEach((_, i) => {
          Vue.set(perPax, i, this.getPerPaxByMinimumOfRange(i));
          if (i !== index) {
            Vue.set(perGroup, i, this.getPerGroupByMinimumOfRange(i));
          }
        });
      } else if (status === "dynamic" || status === "perGroup") {
        margin.percentDynamic[index] = this.calculatePercentByPerPax(index, perGroupValue);
        Vue.set(perPax, index, this.getPerPaxByMinimumOfRange(index));
      }
    },
    onSupplementInput(key, value) {
      if (this.supplementsDisabled) return;
      if (key !== "triple" && key !== "quad") return;
      if (!this.isSupplementOverridden(key)) return;

      const field = key === "triple" ? "supplementTripleOverride" : "supplementQuadOverride";
      if (value === "" || value == null || Number.isNaN(Number(value))) {
        // Keep the field unlocked; empty input falls back to the calculated amount.
        Vue.set(this.data, field, this.getCalculatedSupplementDisplay(key));
      } else {
        Vue.set(this.data, field, Number(value));
      }
    },
    onSupplementBlur(key) {
      if (this.supplementsDisabled) return;
      if (key !== "triple" && key !== "quad") return;
      if (!this.isSupplementOverridden(key)) return;
      this.emitSupplementOverridesChanged();
    },
    isSupplementOverridden(key) {
      if (key === "triple") return this.isTripleOverridden;
      if (key === "quad") return this.isQuadOverridden;
      return false;
    },
    getCalculatedSupplementDisplay(key) {
      if (key === "triple") return quoteUtils.roundDownTo5(this.supplement.triple);
      if (key === "quad") return quoteUtils.roundDownTo5(this.supplement.quad);
      return null;
    },
    toggleSupplementEditable(key) {
      if (this.supplementsDisabled) return;
      if (key !== "triple" && key !== "quad") return;

      const field = key === "triple" ? "supplementTripleOverride" : "supplementQuadOverride";
      if (this.isSupplementOverridden(key)) {
        Vue.set(this.data, field, null);
      } else {
        Vue.set(this.data, field, this.getCalculatedSupplementDisplay(key));
      }
      this.emitSupplementOverridesChanged();
    },
    emitSupplementOverridesChanged() {
      this.$emit("supplement-overrides-changed", {
        supplementTripleOverride: this.data.supplementTripleOverride ?? null,
        supplementQuadOverride: this.data.supplementQuadOverride ?? null,
      });
    },
    calculatePerPaxByPercent(index, percent) {
      const totalCost = this.getTotalCosts();
      return (totalCost[index] * percent) / 100 + totalCost[index];
    },
    calculatePercentByPerPax(index, value) {
      const totalCost = this.getTotalCosts();
      const totalCostWithNumber = totalCost[index] * this.data.peopleRanges[index].min;
      return this.roundToTwoDecimals(((Number(value) + totalCostWithNumber) / totalCostWithNumber - 1) * 100);
    },
    getPerPaxByMinimumOfRange(index) {
      const percent =
        this.data.margin.status === "fixed" ? this.data.margin.percentFixed : this.data.margin.percentDynamic[index];
      const perPax = this.calculatePerPaxByPercent(index, percent);
      return this.roundToTwoDecimals(perPax);
    },
    getPerGroupByMinimumOfRange(index) {
      const percent =
        this.data.margin.status === "fixed" ? this.data.margin.percentFixed : this.data.margin.percentDynamic[index];
      const totalCost = this.getTotalCosts();
      const minimum = this.data.peopleRanges[index].min;
      return Math.round(totalCost[index] * minimum * (percent / 100));
    },
    getTotalCosts() {
      return quoteUtils.getTotalCosts(this.data.costs, this.data.logisticsCosts, this.data.peopleRanges);
    },
    updateMargins() {
      if (!this.data?.margin || !this.data?.peopleRanges) return;

      for (let i = 0; i < this.data.peopleRanges.length; i++) {
        const perGroup = this.getPerGroupByMinimumOfRange(i);
        const perPax = this.getPerPaxByMinimumOfRange(i);

        if (this.data.margin.perGroup[i] !== perGroup) {
          Vue.set(this.data.margin.perGroup, i, perGroup);
        }
        if (this.data.margin.perPax[i] !== perPax) {
          Vue.set(this.data.margin.perPax, i, perPax);
        }
      }
    },
    roundToTwoDecimals(number) {
      return Number(number.toFixed(2));
    },
    getCurrency() {
      const workingEnv = this.$store.getters["AccountStore/environment"];
      const source = moneyService.getCurrencySource(workingEnv);
      return source.currency_symbol;
    },
  },
  mounted() {
    this.updateMargins();
  },
  watch: {
    "value.costs": {
      handler() {
        this.updateMargins();
      },
      deep: true,
      immediate: true,
    },
    "value.logisticsCosts": {
      handler() {
        this.updateMargins();
      },
      deep: true,
      immediate: true,
    },
    "value.peopleRanges": {
      handler() {
        this.updateMargins();
      },
      deep: true,
      immediate: true,
    },
  },
};
</script>

<style scoped>
.quote-section-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 10px;
}

.quote-section-block {
  display: inline-block;
  width: max-content;
  max-width: 100%;
  padding: 10px 12px;
  border: 1px solid #d0d0d0;
  border-radius: 8px;
  background: #fff;
  box-sizing: border-box;
  vertical-align: top;
}

.quote-margin-option--fixed {
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid #efefef;
}

.quote-margin-option__header,
.quote-margin-mode {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #333;
  cursor: pointer;
  white-space: nowrap;
}

.quote-margin-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-left: 24px;
}

.quote-margin-label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #666;
}

.quote-margin-modes {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 16px;
  margin-bottom: 8px;
}

.quote-margin-table-row {
  display: flex;
  align-items: flex-start;
  gap: 48px;
}

.quote-margin-table-wrap {
  overflow-x: auto;
  flex: 0 1 auto;
}

.quote-margin-table {
  border-collapse: collapse;
  width: max-content;
}

.quote-margin-table th {
  padding: 4px 6px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #666;
  text-align: left;
  border-bottom: 1px solid #e0e0e0;
  white-space: nowrap;
}

.quote-margin-help-label {
  cursor: help;
  border-bottom: 1px dotted #bbb;
}

.quote-margin-table td {
  padding: 4px 6px;
  vertical-align: middle;
  border-bottom: 1px solid #efefef;
  white-space: nowrap;
}

.quote-margin-table__range,
.quote-margin-table__label {
  font-weight: 400;
  font-size: 13px;
  color: #555;
  padding-right: 8px !important;
}

.quote-margin-inline {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
}

.quote-margin-inline >>> .v-input,
.quote-margin-inline >>> .v-text-field {
  width: 100px;
  min-width: 100px;
  flex: 0 0 100px;
}

.quote-margin-unit {
  color: #666;
  font-size: 12px;
  white-space: nowrap;
}

.quote-margin-lock {
  margin-left: 2px;
}

.quote-margin-supplements {
  flex: 0 0 auto;
  align-self: flex-end;
  padding: 4px 8px 6px;
  background: #e8e8e8;
  border: 1px solid #d0d0d0;
  border-radius: 6px;
  box-sizing: border-box;
}
</style>
